import { flexRender } from "@tanstack/react-table";
import React, { forwardRef, useEffect, useMemo, useState } from "react";
import { ActionColumn, CustomTableTextArea, TableCellRoot } from "./TableCell.styles";
import { colorPrimary, colorRed } from "@/constants/colors";
import { ITableCell } from "./TableCell.props";
import { TQuestionRow } from "@/types/tableTypes";
import { Autocomplete, Popper } from "@mui/material";
// import VirtualListBox from "../VirtualListBox/VirtualListBox";@
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateLeft } from "@fortawesome/free-solid-svg-icons";
import cn from "classnames";
import styles from "./TableCell.module.scss";

const TableCellInner = <T,>(
	{
		cell,
		isEditable = false,
		focusedCell,
		cellIndex,
		setChangedCellValue,
		isFocused,
		setFocusedCell,
		keyDownHandler,
		isHover,
		autocompleteOptions,
		children,
		title = "",
	}: ITableCell<T>,
	ref: React.Ref<HTMLTableCellElement>
) => {
	const [defaultValue, setDefaultValue] = useState(cell.getValue());
	const [value, setValue] = useState(defaultValue);
	const [isChanged, setIsChanged] = useState(false);
	const originalValue = useMemo(() => cell.getValue(), []);

	const original = cell.row.original as TQuestionRow; //Позже заменить на джененрик или как то иначе обобщить

	const isSelected = focusedCell?.row === cellIndex.row && focusedCell.column === cellIndex.column;

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setValue(e.target.value);
	};

	const handleReset = (e: React.MouseEvent) => {
		e.stopPropagation();
		setValue(originalValue);
	};

	const handleSaveNewCellValue = () => {
		if (!setChangedCellValue || value === defaultValue) {
			return;
		}

		setChangedCellValue((prev) => {
			const id = original.id;
			const columnId = cell.column.id;

			const index = prev.findIndex((item) => Object.hasOwn(item, id));

			if (index !== -1) {
				const updated = [...prev];
				const existing = updated[index];
				const existingData = existing[id];

				updated[index] = {
					[id]: {
						...existingData,
						[columnId]: value as string,
					},
				};

				return updated;
			} else {
				return [
					...prev,
					{
						[id]: {
							[columnId]: value as string,
						},
					},
				];
			}
		});

		setDefaultValue(value);
	};

	useEffect(() => {
		if (value === defaultValue) return;

		const timerId = setTimeout(() => {
			handleSaveNewCellValue();
		}, 300);

		return () => clearTimeout(timerId);
	}, [value, defaultValue]);

	useEffect(() => {
		const table = cell.getContext().table;
		const rowId = cell.row.id;

		if (value !== defaultValue) {
			table.setRowSelection((prev) => ({
				...prev,
				[rowId]: true,
			}));
		}

		setIsChanged(originalValue !== value);
	}, [value]);

	if (cell.column.id === "select") {
		const checked = cell.row.getIsSelected();

		return (
			<td
				style={{
					width: cell.column.getSize(),
					paddingLeft: "6px",
					opacity: isHover || checked ? "1" : "0",
					transition: "linear 0.2s",
					verticalAlign: "top",
				}}
			>
				<ActionColumn>
					{flexRender(cell.column.columnDef.cell, cell.getContext())}
					{children !== null ? children : <></>}
				</ActionColumn>
			</td>
		);
	}

	return isSelected && isEditable && isFocused ? (
		<TableCellRoot title={title}>
			{autocompleteOptions && autocompleteOptions?.length > 0 ? (
				<Autocomplete
					freeSolo
					autoHighlight
					disableListWrap
					openOnFocus
					options={autocompleteOptions}
					value={value}
					onChange={(_, newValue) => setValue(newValue ?? "")}
					// ListboxComponent={VirtualListBox}
					renderInput={(params) => (
						<CustomTableTextArea
							{...params}
							multiline
							value={value}
							onChange={handleChange}
							onKeyDown={keyDownHandler}
							inputRef={ref}
							onFocus={setFocusedCell}
							tabIndex={isFocused ? 0 : -1}
							focused
							InputProps={{
								...params.InputProps,
								sx: {
									"&.MuiOutlinedInput-root": {
										paddingRight: "0px !important", // убирает правый паддинг
										margin: "0 !important",
									},
								},
							}}
						/>
					)}
					renderOption={(props, option) => (
						<li
							{...props}
							style={{
								lineHeight: "1.4",
								fontSize: 11,
								textAlign: "left",
								padding: "4px",
							}}
						>
							{option as string}
						</li>
					)}
					PopperComponent={(props) => <Popper {...props} sx={{ boxShadow: `0 0 5px 1px ${colorPrimary}`, borderRadius: 3 }} />}
					sx={{ padding: 0, ".MuiOutlinedInput-root": { padding: "0 0 0 3px", m: 0 }, "MuiAutocomplete-root": { p: 0 } }}
				/>
			) : (
				<CustomTableTextArea
					multiline
					value={value}
					onChange={handleChange}
					onKeyDown={keyDownHandler}
					inputRef={ref}
					onFocus={setFocusedCell}
					tabIndex={isFocused ? 0 : -1}
					focused={true}
				/>
			)}
		</TableCellRoot>
	) : (
		<td ref={ref} className={cn(styles["default-column"])} style={{ width: cell.column.getSize() }} onClick={setFocusedCell}>
			<FontAwesomeIcon
				className={cn(styles["undo-icon"])}
				icon={faRotateLeft}
				size="lg"
				onClick={handleReset}
				style={{ color: colorRed, display: isChanged ? "block" : "none" }}
			/>
			{flexRender(cell.column.columnDef.cell, cell.getContext())}
		</td>
	);
};

const TableCell = forwardRef(TableCellInner) as <T>(props: ITableCell<T> & { ref?: React.Ref<HTMLTableCellElement> }) => ReturnType<typeof TableCellInner>;

export default TableCell;
